import classNames from "classnames";
import { CustomValuesList } from "PFApp/components/custom_values_list/custom_values_list";
import { FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useMatchAttributeSidepanel } from "PFApp/hooks/activity_form/use_match_attribute_sidepanel";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { ActionIcons, useInputIcons } from "PFComponents/action_icons";
import { Button } from "PFComponents/button";
import { Flex, Inline } from "PFComponents/containers";
import { TooltipIcon } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { isRankable } from "PFCore/helpers/custom_type";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel, ActivityPureCustomValue, CustomType, Id, Profile } from "PFCore/types";
import { CSSProperties, RefObject } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "../../../../tabular_roles_form.module.scss";
import matchCss from "./match_attribute_cell.module.scss";

type MatchAttributeCellProps = {
  customType: CustomType;
  isMultiple: boolean;
  connectedValuesIds: Id[];
  roleFormKey: RoleFormKey;
  value: ActivityPureCustomValue[];
  title: string;
  formattedTitle?: string;
  onChange: (newValue: ActivityPureCustomValue[]) => void;
  disabled?: boolean;
  error: string;
  activityCreator: Profile | null;
  tabIndex?: number;
  style?: CSSProperties;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const MatchAttributeCell = ({
  customType,
  isMultiple,
  connectedValuesIds,
  roleFormKey,
  value,
  title,
  formattedTitle,
  onChange,
  disabled,
  error,
  activityCreator,
  tabIndex,
  style,
  buttonRef
}: MatchAttributeCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.matchAttributes" });
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { t: tTranslation } = useTranslation();
  const { data: currentProfile } = useCurrentProfile();
  const roleTemplate = useTemplate(TemplateKey.Role)!;

  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });

  const { handleSidePanelOpen } = useMatchAttributeSidepanel({
    value,
    customType,
    isMultiple,
    template: roleTemplate,
    activityCreator,
    connectedValuesIds,
    onChange
  });

  const rankable = isRankable(customType);
  const accessLevel = activityCustomTypesAccessLevels(currentProfile, activityCreator ?? null)[customType.id];
  const locked = accessLevel !== AccessLevel.ReadWrite;
  const reachedMaxValues = !isMultiple && value.length >= 1;
  const addDisabled = disabled || reachedMaxValues;

  const actionIcons = useInputIcons({
    onClear: value.length > 0 ? () => onChange([]) : undefined,
    label: title,
    locked,
    disabled
  });
  const formattedFieldTitle = tBlueprints("addCustomValuesToFieldInRole", {
    fieldName: title,
    roleName: roleName || tBlueprints("emptyRoleName")
  });

  return (
    <div
      className={classNames(css.cellFormSection, { [css.error]: error, [matchCss.locked]: locked })}
      aria-label={formattedTitle}
      aria-invalid={error ? "true" : undefined}
      style={style}
      tabIndex={-1}
    >
      <Inline alignItems="start" gap="spacingSm" justifyContent="space-between">
        {value.length > 0 ? (
          <CustomValuesList<ActivityPureCustomValue>
            customValues={value}
            limit={Infinity}
            compact
            customOrder={[
              ["importance", "required_experience"],
              ["desc", "desc"]
            ]}
            injectCustomPillProps={(customValue) => ({
              requiredExperience: rankable ? customValue.required_experience : undefined,
              isRequirement: true,
              onClick: disabled || locked ? undefined : () => handleSidePanelOpen(customValue)
            })}
            tabIndex={tabIndex}
          />
        ) : (
          <Typography variant="bodyRegular">{t("noCustomValuesAdded")}</Typography>
        )}
        <div className={css.stickyActionButtons}>
          <ActionIcons actionIcons={actionIcons} tabIndex={tabIndex} />
          {locked && (
            <TooltipIcon
              iconName="locked"
              iconProps={{ size: "sm" }}
              content={tTranslation("customValues.tips.noPermissionsTip")}
            />
          )}
        </div>
      </Inline>
      {!locked && (
        <Flex className={css.stickyEditButtonContainer}>
          <Button
            ref={buttonRef}
            kind="text"
            className={css.editButton}
            onClick={() => handleSidePanelOpen()}
            text={t("add")}
            aria-label={formattedFieldTitle}
            title={formattedFieldTitle}
            disabled={addDisabled}
            tabIndex={tabIndex}
          />
        </Flex>
      )}
    </div>
  );
};
